import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
} from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  ComposedChart,
  Area,
} from 'recharts';
import { useOptimization } from '../../context/OptimizationContext';
import { calculateCombinedWinner } from '../../utils/combinedWinnerUtils';
import { calculateQTIForResults } from '../../utils/qti';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import Chip from '@mui/material/Chip';
import BoxPlotChart from './BoxPlotChart';
import ChartPanelCard from './ChartPanelCard';
import {
  RESULT_ALGORITHM_COLORS,
  RESULT_ALGORITHM_NAMES,
  getResultAlgorithmKeysForResults,
} from '../../utils/algorithmRegistry';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function isFiniteMetricValue(value: number | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`charts-tabpanel-${index}`}
      aria-labelledby={`charts-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const STANDARD_CHART_HEIGHT = 400;
const EXPANDED_CHART_HEIGHT = 560;
const MULTI_METRIC_CHART_HEIGHT = 420;
const EXPANDED_MULTI_METRIC_CHART_HEIGHT = 620;
const BOX_PLOT_CHART_HEIGHT = 450;
const EXPANDED_BOX_PLOT_CHART_HEIGHT = 620;
const CONVERGENCE_GRID_STROKE = 'rgba(148,163,184,0.32)';
const CONVERGENCE_AXIS_STROKE = '#64748b';

const toConvergenceGradientId = (name: string) =>
  `charts-convergence-${name.replace(/[^a-z0-9]/gi, '').toLowerCase() || 'series'}`;

function renderEmptyState(message: string, height: number = STANDARD_CHART_HEIGHT) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height }}>
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Box>
  );
}

interface ChartsViewProps {
  activeTab?: number;
  onTabChange?: (newValue: number) => void;
}

const ChartsView: React.FC<ChartsViewProps> = ({ activeTab, onTabChange }) => {
  const { results, convergenceData } = useOptimization();
  const [internalValue, setInternalValue] = useState(0);
  const value = Math.min(activeTab ?? internalValue, 13);

  const fitnessLabel = useMemo(() => {
    if (!results?.configuration?.fitnessMode || results.configuration.fitnessMode === 'qos-only') {
      return 'Fitness (QoS)';
    }
    const weights = results.configuration.energyAwareWeights;
    if (weights) {
      return `Fitness (Composite: ${(weights.fitness * 100).toFixed(0)}% QoS, ${(weights.energy * 100).toFixed(0)}% Energy)`;
    }
    return 'Fitness (Energy-Aware)';
  }, [results]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    if (activeTab === undefined) {
      setInternalValue(newValue);
    }
    onTabChange?.(newValue);
  };

  const chartColors = RESULT_ALGORITHM_COLORS;
  const summaryMetricColors = {
    fitness: chartColors.ga,
    energy: chartColors.culture,
    cost: chartColors.frog,
    responseTime: chartColors.sflaga,
    executionTime: '#455a64',
    qti: '#c2185b',
    compositeScore: chartColors.pso,
  };
  const activeAlgorithmKeys = useMemo(
    () => getResultAlgorithmKeysForResults(results),
    [results]
  );
  const activeAlgorithmSeries = useMemo(
    () =>
      activeAlgorithmKeys.map((key) => ({
        key,
        dataKey: RESULT_ALGORITHM_NAMES[key],
        color: chartColors[key],
      })),
    [activeAlgorithmKeys, chartColors]
  );

  // Prepare fitness comparison data
  const fitnessComparisonData = useMemo(() => {
    if (!results) return [];
    return [
      {
        name: 'Algorithms',
        ...Object.fromEntries(
          activeAlgorithmSeries.map(({ key, dataKey }) => [dataKey, results[key]?.fitness ?? 0])
        ),
      },
    ];
  }, [results, activeAlgorithmSeries]);

  const getAggregatedQosValue = useCallback((key: (typeof activeAlgorithmKeys)[number]): number | undefined => {
    const algorithmResult = results?.[key];
    if (!algorithmResult) {
      return undefined;
    }

    if (isFiniteMetricValue(algorithmResult.aggregatedQos)) {
      return algorithmResult.aggregatedQos;
    }

    return results?.configuration?.fitnessMode === 'qos-only' ? algorithmResult.fitness : undefined;
  }, [results]);

  const aggregatedQosData = useMemo(() => {
    if (!results) return [];
    return [
      {
        name: 'Aggregated QoS',
        ...Object.fromEntries(
          activeAlgorithmSeries.map(({ key, dataKey }) => [dataKey, getAggregatedQosValue(key)])
        ),
      },
    ];
  }, [results, activeAlgorithmSeries, getAggregatedQosValue]);

  const hasAggregatedQosData = useMemo(
    () => activeAlgorithmKeys.some((key) => isFiniteMetricValue(getAggregatedQosValue(key))),
    [activeAlgorithmKeys, getAggregatedQosValue]
  );

  // Prepare convergence data
  const convergenceSeries = useMemo(() => {
    if (!convergenceData) return [];

    const iterations = new Set<number>();
    const fitnessByAlgorithm = new Map<string, Map<number, number>>();

    activeAlgorithmSeries.forEach(({ key, dataKey }) => {
      const series = new Map<number, number>();
      (convergenceData[key] ?? []).forEach(({ iteration, fitness }) => {
        if (!Number.isFinite(iteration)) {
          return;
        }
        iterations.add(iteration);
        series.set(iteration, fitness);
      });
      fitnessByAlgorithm.set(dataKey, series);
    });

    return Array.from(iterations)
      .sort((a, b) => a - b)
      .map((iteration) => ({
        iteration,
        ...Object.fromEntries(
          activeAlgorithmSeries.map(({ dataKey }) => [
            dataKey,
            fitnessByAlgorithm.get(dataKey)?.get(iteration) ?? null,
          ])
        ),
      }));
  }, [convergenceData, activeAlgorithmSeries]);

  // Similarity data (relative to SFLAGA)
  const similarityData = useMemo(() => {
    if (!results || !results.sflaga) return [];
    const sflagaFitness = results.sflaga.fitness;
    if (sflagaFitness === 0) return [];
    
    return [
      {
        name: 'Similarity',
        ...Object.fromEntries(
          activeAlgorithmSeries
            .filter(({ key }) => key !== 'sflaga')
            .map(({ key, dataKey }) => [
              dataKey,
              results[key]?.fitness ? results[key]!.fitness / sflagaFitness : 0,
            ])
        ),
      },
    ];
  }, [results, activeAlgorithmSeries]);

  // Energy consumption data
  const energyData = useMemo(() => {
    if (!results) return [];
    return [
      {
        name: 'Energy',
        ...Object.fromEntries(
          activeAlgorithmSeries.map(({ key, dataKey }) => [dataKey, results[key]?.energy ?? 0])
        ),
      },
    ];
  }, [results, activeAlgorithmSeries]);

  // Execution cost data
  const costData = useMemo(() => {
    if (!results) return [];
    return [
      {
        name: 'Cost',
        ...Object.fromEntries(
          activeAlgorithmSeries.map(({ key, dataKey }) => [dataKey, results[key]?.cost ?? 0])
        ),
      },
    ];
  }, [results, activeAlgorithmSeries]);

  // Response Time data
  const responseTimeData = useMemo(() => {
    if (!results) return [];
    return [
      {
        name: 'Response Time',
        ...Object.fromEntries(
          activeAlgorithmSeries.map(({ key, dataKey }) => [
            dataKey,
            results[key]?.responseTime ?? 0,
          ])
        ),
      },
    ];
  }, [results, activeAlgorithmSeries]);

  // Execution Time data
  const executionTimeData = useMemo(() => {
    if (!results) return [];
    // CRITICAL: Do NOT convert missing executionTime to 0 - it corrupts statistics
    // Use undefined for missing values (chart library will handle appropriately)
    return [
      {
        name: 'Execution Time',
        ...Object.fromEntries(
          activeAlgorithmSeries.map(({ key, dataKey }) => [
            dataKey,
            results[key]?.executionTime,
          ])
        ),
      },
    ];
  }, [results, activeAlgorithmSeries]);

  // Derived QTI data using fitness / (1 + normalized execution time)
  const qtiEntries = useMemo(() => {
    if (!results) return [];

    return calculateQTIForResults(results)
      .filter((entry) => Number.isFinite(entry.qti));
  }, [results]);

  const qtiData = useMemo(() => {
    return qtiEntries.map((entry) => ({
      name: entry.algorithmName,
      QTI: entry.qti,
      Fitness: entry.fitness,
      ExecutionTime: entry.executionTime,
      ExecutionTimeNorm: entry.executionTimeNorm,
    }));
  }, [qtiEntries]);

  // Normalized performance data (all metrics normalized to 0-1 scale)
  const normalizedData = useMemo(() => {
    if (!results) return [];

    const algorithmsWithResults = activeAlgorithmSeries.filter(
      ({ key }) => results[key] !== undefined
    );
    const fitnessValues = algorithmsWithResults.map(({ key }) => results[key]!.fitness);
    const energyValues = algorithmsWithResults.map(({ key }) => results[key]!.energy);
    const costValues = algorithmsWithResults.map(({ key }) => results[key]!.cost);
    const responseTimeValues = algorithmsWithResults.map(
      ({ key }) => results[key]!.responseTime
    );

    if (algorithmsWithResults.length === 0) return [];

    const maxFitness = Math.max(...fitnessValues);
    const minFitness = Math.min(...fitnessValues);
    const maxEnergy = Math.max(...energyValues);
    const minEnergy = Math.min(...energyValues);
    const maxCost = Math.max(...costValues);
    const minCost = Math.min(...costValues);
    const maxResponseTime = Math.max(...responseTimeValues);
    const minResponseTime = Math.min(...responseTimeValues);
    
    const normalize = (value: number, min: number, max: number, reverse: boolean = false) => {
      if (max === min) return 0.5;
      const normalized = (value - min) / (max - min);
      return reverse ? 1 - normalized : normalized;
    };

    const buildMetricRow = (
      metricName: string,
      getValue: (key: typeof algorithmsWithResults[number]['key']) => number,
      min: number,
      max: number,
      reverse: boolean = false
    ) => ({
      name: metricName,
      ...Object.fromEntries(
        algorithmsWithResults.map(({ key, dataKey }) => [
          dataKey,
          normalize(getValue(key), min, max, reverse),
        ])
      ),
    });

    return [
      buildMetricRow('Fitness', (key) => results[key]!.fitness, minFitness, maxFitness),
      buildMetricRow(
        'Energy Efficiency',
        (key) => results[key]!.energy,
        minEnergy,
        maxEnergy,
        true
      ),
      buildMetricRow(
        'Cost Efficiency',
        (key) => results[key]!.cost,
        minCost,
        maxCost,
        true
      ),
      buildMetricRow(
        'Response Time Efficiency',
        (key) => results[key]!.responseTime,
        minResponseTime,
        maxResponseTime,
        true
      ),
    ];
  }, [results, activeAlgorithmSeries]);

  // Multi-metric summary data
  const multiMetricData = useMemo(() => {
    if (!results) return [];

    const qtiByAlgorithm = new Map(
      qtiEntries.map((entry) => [entry.algorithmKey, entry.qti])
    );

    return activeAlgorithmKeys
      .filter(key => results[key] !== undefined)
      .map((key) => {
        const algorithmResult = results[key]!;
        const compositeScore = results.compositeScores?.[key]?.median;

        return {
          name: RESULT_ALGORITHM_NAMES[key],
          Fitness: algorithmResult.fitness,
          Energy: algorithmResult.energy,
          Cost: algorithmResult.cost,
          ResponseTime: algorithmResult.responseTime,
          ExecutionTime: isFiniteMetricValue(algorithmResult.executionTime)
            ? algorithmResult.executionTime
            : undefined,
          QTI: qtiByAlgorithm.get(key),
          CompositeScore: isFiniteMetricValue(compositeScore)
            ? compositeScore
            : undefined,
        };
      });
  }, [results, activeAlgorithmKeys, qtiEntries]);

  const multiMetricSeriesAvailability = useMemo(
    () => ({
      hasExecutionTime: multiMetricData.some((entry) => isFiniteMetricValue(entry.ExecutionTime)),
      hasQTI: multiMetricData.some((entry) => isFiniteMetricValue(entry.QTI)),
      hasCompositeScore: multiMetricData.some((entry) =>
        isFiniteMetricValue(entry.CompositeScore)
      ),
    }),
    [multiMetricData]
  );

  // Convergence speed analysis - calculate improvement rate
  const convergenceSpeedData = useMemo(() => {
    if (!convergenceData || convergenceData.ga.length === 0) return [];

    return activeAlgorithmKeys.map((key) => {
      const data = convergenceData[key];
      if (!data || data.length < 2) {
        return { name: RESULT_ALGORITHM_NAMES[key], improvementRate: 0, iterationsTo90Percent: 0 };
      }

      const initialFitness = data[0].fitness;
      const finalFitness = data[data.length - 1].fitness;
      const initialIteration = Number.isFinite(data[0].iteration) ? data[0].iteration : 1;
      const finalIteration = Number.isFinite(data[data.length - 1].iteration)
        ? data[data.length - 1].iteration
        : data.length;
      const iterationSpan = Math.max(1, finalIteration - initialIteration);
      const improvementRate = finalFitness > initialFitness
        ? (finalFitness - initialFitness) / iterationSpan
        : 0;

      // Find the actual generation where 90% of final improvement is reached.
      const targetFitness = initialFitness + (finalFitness - initialFitness) * 0.9;
      let iterationsTo90Percent = finalIteration;
      for (let i = 0; i < data.length; i++) {
        if (data[i].fitness >= targetFitness) {
          iterationsTo90Percent = Number.isFinite(data[i].iteration)
            ? data[i].iteration
            : i + 1;
          break;
        }
      }

      return {
        name: RESULT_ALGORITHM_NAMES[key],
        improvementRate: improvementRate * 1000, // Scale for visibility
        iterationsTo90Percent,
      };
    });
  }, [convergenceData, activeAlgorithmKeys]);

  // Box plot data for energy distribution (requires statistics from multiple runs)
  const energyBoxPlotData = useMemo(() => {
    if (!results || !results.statistics) return [];

    return activeAlgorithmKeys
      .map((key) => {
        const algorithmStats = results.statistics?.[key];
        if (!algorithmStats) {
          return null;
        }

        const stats = algorithmStats.energy;
        const q1 = stats.q1 ?? stats.median;
        const q3 = stats.q3 ?? stats.median;
        const iqr = q3 - q1;
        
        return {
          name: RESULT_ALGORITHM_NAMES[key],
          min: stats.min,
          q1: q1,
          median: stats.median,
          q3: q3,
          max: stats.max,
          mean: stats.mean,
          stdDev: stats.stdDev,
          // For visualization purposes
          lowerWhisker: Math.max(stats.min, q1 - 1.5 * iqr),
          upperWhisker: Math.min(stats.max, q3 + 1.5 * iqr),
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
  }, [results, activeAlgorithmKeys]);

  const combinedWinner = useMemo(
    () => (results ? calculateCombinedWinner(results) : null),
    [results]
  );
  const combinedWinnerScatterData = useMemo(
    () =>
      combinedWinner?.rankedAlgorithms.map((algorithm) => ({
        name: algorithm.name,
        fitness: algorithm.fitness,
        energy: algorithm.energy,
        fitnessRank: algorithm.fitnessRank,
        energyRank: algorithm.energyRank,
      })) ?? [],
    [combinedWinner]
  );

  const hasData = results !== null && convergenceData !== null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <ShowChartIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" component="h2">
          Charts & Visualizations
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {/* First Row of Tabs */}
          <Tabs 
            value={value < 5 ? value : false} 
            onChange={(e, newValue) => {
              if (newValue !== false) handleChange(e, newValue);
            }} 
            variant="scrollable" 
            scrollButtons="auto"
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              minHeight: 48,
              '& .MuiTab-root': { 
                minWidth: { xs: 100, sm: 140 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                textTransform: 'none',
              }
            }}
          >
            <Tab label="Fitness Comparison" />
            <Tab label="Aggregated QoS" />
            <Tab label="Convergence" />
            <Tab label="Similarity" />
            <Tab label="Energy" />
          </Tabs>
          
          {/* Second Row of Tabs */}
          <Tabs 
            value={value >= 5 && value <= 13 ? value - 5 : false} 
            onChange={(e, newValue) => {
              if (newValue !== false) handleChange(e, newValue + 5);
            }} 
            variant="scrollable" 
            scrollButtons="auto"
            sx={{ 
              minHeight: 48,
              '& .MuiTab-root': { 
                minWidth: { xs: 100, sm: 140 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                textTransform: 'none',
              }
            }}
          >
            <Tab label="Cost" />
            <Tab label="Response Time" />
            <Tab label="Execution Time" />
            <Tab label="QTI (Derived)" />
            <Tab label="Normalized" />
            <Tab label="Multi-Metric" />
            <Tab label="Convergence Speed" />
            <Tab label="Combined Winner" />
            <Tab label="Energy Box Plot" />
          </Tabs>
        </Box>
      </Box>

      {/* Fitness Comparison Chart */}
      <TabPanel value={value} index={0}>
        <ChartPanelCard
          title={`${fitnessLabel} Comparison Across Algorithms`}
          info={
            results?.configuration?.fitnessMode === 'energy-aware' ? (
              <>
                This bar chart displays the final <strong>composite fitness scores</strong> for all
                optimization algorithms. Fitness combines QoS quality and energy consumption using
                configurable weights, while cost is reported separately.
                <strong> Higher fitness values indicate better solutions.</strong> Use this chart
                to compare which algorithm achieved the best energy-aware performance.
              </>
            ) : (
              <>
                This bar chart displays the final fitness scores for all optimization algorithms.
                Fitness is calculated based on QoS attributes (Response Time, Availability,
                Throughput, etc.) using a weighted aggregation formula.
                <strong> Higher fitness values indicate better solutions.</strong> Use this chart
                to quickly compare which algorithm achieved the best overall performance.
              </>
            )
          }
          renderContent={(isExpanded) =>
            !hasData ? (
              renderEmptyState(
                'No data available. Please run optimization to view charts.',
                isExpanded ? EXPANDED_CHART_HEIGHT : STANDARD_CHART_HEIGHT
              )
            ) : (
              <ResponsiveContainer
                width="100%"
                height={isExpanded ? EXPANDED_CHART_HEIGHT : STANDARD_CHART_HEIGHT}
              >
                <BarChart data={fitnessComparisonData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis
                    label={{ value: fitnessLabel, angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip />
                  <Legend />
                  {activeAlgorithmSeries.map(({ dataKey, color }) => (
                    <Bar key={dataKey} dataKey={dataKey} fill={color} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )
          }
        />
      </TabPanel>

      {/* Aggregated QoS Chart */}
      <TabPanel value={value} index={1}>
        <ChartPanelCard
          title="Aggregated QoS Comparison Across Algorithms"
          info={
            <>
              This bar chart displays the workflow-aware <strong>aggregated QoS component</strong>{' '}
              for each algorithm before any energy-aware weighting is applied. It uses the same
              sequential QoS aggregation model as the fitness calculator.
              <strong> Higher values indicate better QoS quality.</strong> In energy-aware mode,
              this separates QoS quality from the composite fitness score.
            </>
          }
          renderContent={(isExpanded) =>
            !hasData || !hasAggregatedQosData ? (
              renderEmptyState(
                'No aggregated QoS data available. Run optimization again to populate this chart.',
                isExpanded ? EXPANDED_CHART_HEIGHT : STANDARD_CHART_HEIGHT
              )
            ) : (
              <ResponsiveContainer
                width="100%"
                height={isExpanded ? EXPANDED_CHART_HEIGHT : STANDARD_CHART_HEIGHT}
              >
                <BarChart data={aggregatedQosData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis
                    label={{ value: 'Aggregated QoS', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip />
                  <Legend />
                  {activeAlgorithmSeries.map(({ dataKey, color }) => (
                    <Bar key={dataKey} dataKey={dataKey} fill={color} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )
          }
        />
      </TabPanel>

      {/* Convergence */}
      <TabPanel value={value} index={2}>
        <ChartPanelCard
          title="Convergence Analysis"
          info={
            <>
              This area chart shows how each algorithm&apos;s fitness improves over iterations
              (generations). The X-axis represents iteration numbers, and the Y-axis shows fitness
              values using the same focused scale as the live convergence simulation.
              <strong>
                {' '}
                Steeper upward curves and higher final values indicate better convergence
                performance.
              </strong>{' '}
              Algorithms that converge quickly and reach higher fitness values are generally
              preferred. Use this chart to understand the optimization behavior and convergence
              speed of each algorithm.
            </>
          }
          renderContent={(isExpanded) =>
            !hasData || convergenceSeries.length === 0 ? (
              renderEmptyState(
                'No data available. Please run optimization to view charts.',
                isExpanded ? EXPANDED_CHART_HEIGHT : STANDARD_CHART_HEIGHT
              )
            ) : (
              <ResponsiveContainer
                width="100%"
                height={isExpanded ? EXPANDED_CHART_HEIGHT : STANDARD_CHART_HEIGHT}
              >
                <AreaChart data={convergenceSeries} margin={{ top: 16, right: 16, left: 0, bottom: 8 }}>
                  <defs>
                    {activeAlgorithmSeries.map(({ dataKey, color }) => (
                      <linearGradient key={dataKey} id={toConvergenceGradientId(dataKey)} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={color} stopOpacity={0.72} />
                        <stop offset="95%" stopColor={color} stopOpacity={0.08} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="4 4" stroke={CONVERGENCE_GRID_STROKE} />
                  <XAxis
                    dataKey="iteration"
                    stroke={CONVERGENCE_AXIS_STROKE}
                    tick={{ fontSize: 12, fill: CONVERGENCE_AXIS_STROKE }}
                  />
                  <YAxis
                    stroke={CONVERGENCE_AXIS_STROKE}
                    tick={{ fontSize: 12, fill: CONVERGENCE_AXIS_STROKE }}
                    domain={['auto', 'auto']}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid rgba(15,23,42,0.08)',
                      borderRadius: 8,
                      boxShadow: '0 16px 32px rgba(15,23,42,0.14)',
                    }}
                    labelStyle={{ color: '#0f172a', fontWeight: 700 }}
                    formatter={(value: number, name: string) => [
                      Number.isFinite(value) ? Number(value).toFixed(6) : value,
                      name,
                    ]}
                    labelFormatter={(label) => `Iteration ${label}`}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {activeAlgorithmSeries.map(({ dataKey, color }) => (
                    <Area
                      key={dataKey}
                      type="monotone"
                      dataKey={dataKey}
                      stroke={color}
                      fill={`url(#${toConvergenceGradientId(dataKey)})`}
                      name={dataKey}
                      strokeWidth={2.2}
                      dot={false}
                      connectNulls
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      isAnimationActive={false}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            )
          }
        />
      </TabPanel>

      {/* Similarity Chart */}
      <TabPanel value={value} index={3}>
        <ChartPanelCard
          title="Algorithm Similarity (Relative to SFLAGA)"
          info={
            <>
              This bar chart compares algorithm performance relative to SFLAGA (Shuffled Frog
              Leaping Algorithm - Genetic Algorithm hybrid). Each bar shows the ratio of an
              algorithm&apos;s fitness to SFLAGA&apos;s fitness.
              <strong>
                {' '}
                A value of 1.0 means equal performance, values above 1.0 indicate better
                performance than SFLAGA, and values below 1.0 indicate worse performance.
              </strong>{' '}
              This comparison helps identify which algorithms produce similar or superior results
              compared to the hybrid approach.
            </>
          }
          renderContent={(isExpanded) =>
            !hasData || similarityData.length === 0 ? (
              renderEmptyState(
                'No data available. Please run optimization to view charts.',
                isExpanded ? EXPANDED_CHART_HEIGHT : STANDARD_CHART_HEIGHT
              )
            ) : (
              <ResponsiveContainer
                width="100%"
                height={isExpanded ? EXPANDED_CHART_HEIGHT : STANDARD_CHART_HEIGHT}
              >
                <BarChart data={similarityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {activeAlgorithmSeries
                    .filter(({ key }) => key !== 'sflaga')
                    .map(({ dataKey, color }) => (
                      <Bar key={dataKey} dataKey={dataKey} fill={color} />
                    ))}
                </BarChart>
              </ResponsiveContainer>
            )
          }
        />
      </TabPanel>

      {/* Energy Consumption Chart */}
      <TabPanel value={value} index={4}>
        <ChartPanelCard
          title="Energy Consumption Comparison (Joules)"
          info={
            <>
              This bar chart displays the total energy consumption for each algorithm&apos;s
              solution, measured in Joules. Energy is calculated using the formula: Energy = (a x
              ResponseTime + b x Latency) x Power, where a=0.6, b=0.4, and Power=3.0W.
              <strong> Lower energy consumption indicates more energy-efficient solutions,</strong>{' '}
              which is crucial for cloud computing and resource-constrained environments. This
              metric helps identify algorithms that optimize for energy efficiency.
            </>
          }
          renderContent={(isExpanded) =>
            !hasData ? (
              renderEmptyState(
                'No data available. Please run optimization to view charts.',
                isExpanded ? EXPANDED_CHART_HEIGHT : STANDARD_CHART_HEIGHT
              )
            ) : (
              <ResponsiveContainer
                width="100%"
                height={isExpanded ? EXPANDED_CHART_HEIGHT : STANDARD_CHART_HEIGHT}
              >
                <BarChart data={energyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {activeAlgorithmSeries.map(({ dataKey, color }) => (
                    <Bar key={dataKey} dataKey={dataKey} fill={color} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )
          }
        />
      </TabPanel>

      {/* Execution Cost Chart */}
      <TabPanel value={value} index={5}>
        <ChartPanelCard
          title="Execution Cost Comparison"
          info={
            <>
              This bar chart shows the execution cost for each algorithm&apos;s solution. Cost is
              calculated as the sum of response times across all tasks, multiplied by a cost
              coefficient (typically 1.0).
              <strong> Lower execution costs represent more cost-effective solutions,</strong>{' '}
              which is important for cloud service composition where computational resources are
              billed based on usage. Use this chart to compare the economic efficiency of different
              optimization approaches.
            </>
          }
          renderContent={(isExpanded) =>
            !hasData ? (
              renderEmptyState(
                'No data available. Please run optimization to view charts.',
                isExpanded ? EXPANDED_CHART_HEIGHT : STANDARD_CHART_HEIGHT
              )
            ) : (
              <ResponsiveContainer
                width="100%"
                height={isExpanded ? EXPANDED_CHART_HEIGHT : STANDARD_CHART_HEIGHT}
              >
                <BarChart data={costData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {activeAlgorithmSeries.map(({ dataKey, color }) => (
                    <Bar key={dataKey} dataKey={dataKey} fill={color} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )
          }
        />
      </TabPanel>

      {/* Response Time Chart */}
      <TabPanel value={value} index={6}>
        <ChartPanelCard
          title="Response Time Comparison"
          info={
            <>
              This bar chart displays the total response time for each algorithm&apos;s solution,
              measured in milliseconds. Response time is a critical QoS metric that represents the
              time taken to execute all tasks in the service composition.
              <strong> Lower response times indicate faster solutions,</strong> which are essential
              for real-time applications and user experience. This metric helps identify algorithms
              that optimize for latency and system responsiveness.
            </>
          }
          renderContent={(isExpanded) =>
            !hasData ? (
              renderEmptyState(
                'No data available. Please run optimization to view charts.',
                isExpanded ? EXPANDED_CHART_HEIGHT : STANDARD_CHART_HEIGHT
              )
            ) : (
              <ResponsiveContainer
                width="100%"
                height={isExpanded ? EXPANDED_CHART_HEIGHT : STANDARD_CHART_HEIGHT}
              >
                <BarChart data={responseTimeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {activeAlgorithmSeries.map(({ dataKey, color }) => (
                    <Bar key={dataKey} dataKey={dataKey} fill={color} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )
          }
        />
      </TabPanel>

      {/* Execution Time Chart */}
      <TabPanel value={value} index={7}>
        <ChartPanelCard
          title="Execution Time Comparison"
          info={
            <>
              This bar chart displays the execution time for each algorithm, measured in
              milliseconds. Execution time represents the actual computational time required by
              each algorithm to complete the optimization process.
              <strong> Lower execution times indicate faster algorithms,</strong> which is crucial
              for real-time applications and when computation resources are limited. This metric
              helps identify algorithms that provide a good balance between solution quality and
              computational efficiency.
            </>
          }
          renderContent={(isExpanded) =>
            !hasData ? (
              renderEmptyState(
                'No data available. Please run optimization to view charts.',
                isExpanded ? EXPANDED_CHART_HEIGHT : STANDARD_CHART_HEIGHT
              )
            ) : (
              <ResponsiveContainer
                width="100%"
                height={isExpanded ? EXPANDED_CHART_HEIGHT : STANDARD_CHART_HEIGHT}
              >
                <BarChart data={executionTimeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis label={{ value: 'Time (ms)', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Legend />
                  {activeAlgorithmSeries.map(({ dataKey, color }) => (
                    <Bar key={dataKey} dataKey={dataKey} fill={color} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )
          }
        />
      </TabPanel>

      {/* Derived QTI Chart */}
      <TabPanel value={value} index={8}>
        <ChartPanelCard
          title="Quality-Time Index (QTI) Comparison"
          info={
            <>
              This chart shows a <strong>derived reporting indicator</strong>, not an optimization
              objective. QTI is computed as <strong>Fitness / (1 + normalized execution time)</strong>,
              where execution time is min-max normalized across algorithms for the current result
              set. <strong>Higher QTI indicates better solution quality relative to computational overhead.</strong>
            </>
          }
          renderContent={(isExpanded) =>
            !hasData || qtiData.length === 0 ? (
              renderEmptyState(
                'QTI requires both fitness and execution-time data. Please run optimization to view this chart.',
                isExpanded ? EXPANDED_CHART_HEIGHT : STANDARD_CHART_HEIGHT
              )
            ) : (
              <ResponsiveContainer
                width="100%"
                height={isExpanded ? EXPANDED_CHART_HEIGHT : STANDARD_CHART_HEIGHT}
              >
                <BarChart data={qtiData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis label={{ value: 'QTI', angle: -90, position: 'insideLeft' }} />
                  <Tooltip
                    formatter={(value: number, name: string, props: any) => {
                      if (name === 'QTI') {
                        return [value.toFixed(6), 'QTI'];
                      }
                      if (name === 'Fitness') {
                        return [value.toFixed(6), 'Fitness'];
                      }
                      if (name === 'ExecutionTime') {
                        return [`${value.toFixed(2)} ms`, 'Execution Time'];
                      }
                      if (name === 'ExecutionTimeNorm') {
                        return [value.toFixed(4), 'Execution Time (Normalized)'];
                      }
                      return [value, name];
                    }}
                  />
                  <Legend />
                  <Bar dataKey="QTI" fill={chartColors.sflaga} />
                </BarChart>
              </ResponsiveContainer>
            )
          }
        />
      </TabPanel>

      {/* Normalized Performance Chart */}
      <TabPanel value={value} index={9}>
        <ChartPanelCard
          title="Normalized Performance Comparison"
          info={
            <>
              This chart displays all performance metrics normalized to a 0-1 scale, enabling fair
              comparison across different metric types. All metrics are scaled so that higher
              values (closer to 1.0) indicate better performance.
              <strong>
                {' '}
                This normalization allows you to see which algorithm performs best overall across
                multiple dimensions.
              </strong>{' '}
              Efficiency metrics (Energy, Cost, Response Time) are inverted so that lower values
              become higher scores. This visualization is particularly useful for multi-objective
              optimization analysis.
            </>
          }
          renderContent={(isExpanded) =>
            !hasData || normalizedData.length === 0 ? (
              renderEmptyState(
                'No data available. Please run optimization to view charts.',
                isExpanded ? EXPANDED_CHART_HEIGHT : STANDARD_CHART_HEIGHT
              )
            ) : (
              <ResponsiveContainer
                width="100%"
                height={isExpanded ? EXPANDED_CHART_HEIGHT : STANDARD_CHART_HEIGHT}
              >
                <BarChart data={normalizedData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis domain={[0, 1]} />
                  <Tooltip />
                  <Legend />
                  {activeAlgorithmSeries
                    .filter(({ key }) => results?.[key] !== undefined)
                    .map(({ key, dataKey, color }) => (
                      <Bar key={key} dataKey={dataKey} fill={color} name={dataKey} />
                    ))}
                </BarChart>
              </ResponsiveContainer>
            )
          }
        />
      </TabPanel>

      {/* Multi-Metric Summary Chart */}
      <TabPanel value={value} index={10}>
        <ChartPanelCard
          title="Multi-Metric Performance Summary"
          info={
            <>
              This composed chart combines the main optimization outputs with runtime-aware derived
              metrics. It compares Fitness, Energy, Cost, Response Time, and adds Execution Time
              plus QTI and Composite Score when available. The left Y-axis is used for
              score-oriented metrics, while the right Y-axis shows operational metrics.
              <strong>
                {' '}
                This view is useful when you want one place to inspect quality, efficiency, and
                runtime trade-offs together.
              </strong>{' '}
              Use the tooltip to read exact values for each series.
            </>
          }
          renderContent={(isExpanded) =>
            !hasData || multiMetricData.length === 0 ? (
              renderEmptyState(
                'No data available. Please run optimization to view charts.',
                isExpanded
                  ? EXPANDED_MULTI_METRIC_CHART_HEIGHT
                  : MULTI_METRIC_CHART_HEIGHT
              )
            ) : (
              <ResponsiveContainer
                width="100%"
                height={
                  isExpanded
                    ? EXPANDED_MULTI_METRIC_CHART_HEIGHT
                    : MULTI_METRIC_CHART_HEIGHT
                }
              >
                <ComposedChart
                  data={multiMetricData}
                  margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis
                    yAxisId="left"
                    label={{
                      value: 'Fitness, QTI, Composite Score',
                      angle: -90,
                      position: 'insideLeft',
                    }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    label={{
                      value: 'Energy, Cost, Response / Execution Time',
                      angle: 90,
                      position: 'insideRight',
                    }}
                  />
                  <Tooltip
                    formatter={(value: number | string, name: string) => {
                      if (typeof value !== 'number' || !Number.isFinite(value)) {
                        return ['N/A', name];
                      }

                      const precision = ['Fitness', 'QTI', 'Composite Score (Median)'].includes(
                        name
                      )
                        ? 4
                        : 2;
                      return [value.toFixed(precision), name];
                    }}
                  />
                  <Legend />
                  <Bar
                    yAxisId="left"
                    dataKey="Fitness"
                    fill={summaryMetricColors.fitness}
                    name="Fitness"
                  />
                  {multiMetricSeriesAvailability.hasQTI && (
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="QTI"
                      stroke={summaryMetricColors.qti}
                      strokeWidth={3}
                      dot={{ r: 4 }}
                      name="QTI"
                    />
                  )}
                  {multiMetricSeriesAvailability.hasCompositeScore && (
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="CompositeScore"
                      stroke={summaryMetricColors.compositeScore}
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ r: 4 }}
                      name="Composite Score (Median)"
                    />
                  )}
                  <Area
                    yAxisId="right"
                    type="monotone"
                    dataKey="Energy"
                    fill={summaryMetricColors.energy}
                    stroke={summaryMetricColors.energy}
                    fillOpacity={0.45}
                    name="Energy (Joules)"
                  />
                  <Area
                    yAxisId="right"
                    type="monotone"
                    dataKey="Cost"
                    fill={summaryMetricColors.cost}
                    stroke={summaryMetricColors.cost}
                    fillOpacity={0.35}
                    name="Cost"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="ResponseTime"
                    stroke={summaryMetricColors.responseTime}
                    strokeWidth={2}
                    name="Response Time (ms)"
                  />
                  {multiMetricSeriesAvailability.hasExecutionTime && (
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="ExecutionTime"
                      stroke={summaryMetricColors.executionTime}
                      strokeWidth={2}
                      strokeDasharray="3 3"
                      dot={{ r: 4 }}
                      name="Execution Time (ms)"
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            )
          }
        />
      </TabPanel>

      {/* Convergence Speed Chart */}
      <TabPanel value={value} index={11}>
        <ChartPanelCard
          title="Convergence Speed Analysis"
          info={
            <>
              This chart analyzes the convergence speed of each algorithm through two metrics:
              Improvement Rate (fitness gain per iteration, scaled) and Iterations to 90%
              Performance (number of iterations needed to reach 90% of final improvement).
              <strong> Higher improvement rates and fewer iterations indicate faster convergence,</strong>{' '}
              which is crucial for time-constrained optimization problems. This analysis helps
              identify algorithms that balance convergence speed with solution quality, valuable
              for real-world applications where computation time is limited.
            </>
          }
          renderContent={(isExpanded) =>
            !hasData || convergenceSpeedData.length === 0 ? (
              renderEmptyState(
                'No data available. Please run optimization to view charts.',
                isExpanded ? EXPANDED_CHART_HEIGHT : STANDARD_CHART_HEIGHT
              )
            ) : (
              <ResponsiveContainer
                width="100%"
                height={isExpanded ? EXPANDED_CHART_HEIGHT : STANDARD_CHART_HEIGHT}
              >
                <ComposedChart data={convergenceSpeedData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis
                    yAxisId="left"
                    label={{
                      value: 'Improvement Rate (scaled)',
                      angle: -90,
                      position: 'insideLeft',
                    }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    label={{ value: 'Iterations to 90%', angle: 90, position: 'insideRight' }}
                  />
                  <Tooltip />
                  <Legend />
                  <Bar
                    yAxisId="left"
                    dataKey="improvementRate"
                    fill={chartColors.ga}
                    name="Improvement Rate (x1000)"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="iterationsTo90Percent"
                    stroke={chartColors.sflaga}
                    strokeWidth={3}
                    name="Iterations to 90% Performance"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )
          }
        />
      </TabPanel>

      {/* Combined Winner Chart */}
      <TabPanel value={value} index={12}>
        <ChartPanelCard
          title="Combined Winner Analysis (Energy-First Ranking)"
          info={
            <>
              This chart shows the energy-first winner logic: algorithms are ranked by lowest
              energy first. Composite score is the secondary selector when available, and fitness
              is used after that. The chart compares energy rank and fitness rank for each
              algorithm. Lower ranks are better.
              <strong> Perfect winners still have rank #1 in both metrics simultaneously.</strong>
            </>
          }
          renderContent={(isExpanded) => {
            const chartHeight = isExpanded ? EXPANDED_CHART_HEIGHT : STANDARD_CHART_HEIGHT;

            if (!results) {
              return renderEmptyState(
                'No data available. Please run optimization to view combined winner analysis.',
                chartHeight
              );
            }

            if (!combinedWinner) {
              return renderEmptyState('No combined winner data available.', chartHeight);
            }

            return (
              <Box>
                {combinedWinner.hasPerfectWinner && combinedWinner.perfectWinners.length > 0 ? (
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      bgcolor: 'success.light',
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: 'success.main',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <EmojiEventsIcon sx={{ color: 'success.main', mr: 1 }} />
                      <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 700 }}>
                        Perfect Winner(s)
                      </Typography>
                    </Box>
                    {combinedWinner.perfectWinners.map((winner) => (
                      <Typography
                        key={winner.key}
                        variant="body1"
                        sx={{ fontFamily: 'monospace', color: 'success.dark' }}
                      >
                        {winner.name}: Fitness = {winner.fitness.toFixed(6)} (#1), Energy ={' '}
                        {winner.energy.toFixed(6)} J (#1)
                      </Typography>
                    ))}
                  </Box>
                ) : combinedWinner.bestAlternative ? (
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      bgcolor: 'warning.light',
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: 'warning.main',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <EmojiEventsIcon sx={{ color: 'warning.main', mr: 1 }} />
                      <Typography variant="h6" sx={{ color: 'warning.main', fontWeight: 700 }}>
                        Best Alternative
                      </Typography>
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{ fontFamily: 'monospace', color: 'warning.dark' }}
                    >
                      {combinedWinner.bestAlternative.name}: Fitness ={' '}
                      {combinedWinner.bestAlternative.fitness.toFixed(6)} (Rank #
                      {combinedWinner.bestAlternative.fitnessRank}), Energy ={' '}
                      {combinedWinner.bestAlternative.energy.toFixed(6)} J (Rank #
                      {combinedWinner.bestAlternative.energyRank})
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 1 }}
                    >
                      No algorithm is #1 in both. This is the lowest-energy candidate with the
                      strongest secondary score.
                    </Typography>
                  </Box>
                ) : null}

                <ResponsiveContainer width="100%" height={chartHeight}>
                  <ComposedChart data={combinedWinnerScatterData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis
                      yAxisId="left"
                      label={{ value: 'Fitness Rank', angle: -90, position: 'insideLeft' }}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      label={{ value: 'Energy Rank', angle: 90, position: 'insideRight' }}
                    />
                    <Tooltip
                      formatter={(value: number, name: string, props: any) => {
                        if (name === 'Fitness Rank') {
                          return [
                            `${value} (Fitness: ${props.payload.fitness.toFixed(6)})`,
                            'Fitness Rank',
                          ];
                        }
                        if (name === 'Energy Rank') {
                          return [
                            `${value} (Energy: ${props.payload.energy.toFixed(6)} J)`,
                            'Energy Rank',
                          ];
                        }
                        return value;
                      }}
                      labelFormatter={(label) => `Algorithm: ${label}`}
                    />
                    <Legend />
                    <Bar
                      yAxisId="left"
                      dataKey="fitnessRank"
                      fill="#1976d2"
                      name="Fitness Rank (Lower is Better)"
                      opacity={0.7}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="energyRank"
                      stroke="#ed6c02"
                      strokeWidth={3}
                      name="Energy Rank (Lower is Better)"
                      dot={{ r: 6, fill: '#ed6c02' }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Combined Ranking
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {combinedWinner.rankedAlgorithms.map((algorithm, index) => (
                      <Box
                        key={algorithm.key}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 2,
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: algorithm.isPerfectWinner
                            ? 'rgba(76, 175, 80, 0.1)'
                            : algorithm.isBestAlternative
                              ? 'rgba(255, 193, 7, 0.1)'
                              : 'transparent',
                          border:
                            algorithm.isPerfectWinner || algorithm.isBestAlternative
                              ? '1px solid'
                              : 'none',
                          borderColor: algorithm.isPerfectWinner
                            ? 'success.main'
                            : algorithm.isBestAlternative
                              ? 'warning.main'
                              : 'transparent',
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 30 }}>
                          {index + 1}.
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight:
                              algorithm.isPerfectWinner || algorithm.isBestAlternative ? 700 : 500,
                            minWidth: 100,
                          }}
                        >
                          {algorithm.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                        >
                          Fitness: {algorithm.fitness.toFixed(4)} (Rank #
                          {algorithm.fitnessRank})
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                        >
                          Energy: {algorithm.energy.toFixed(4)} J (Rank #
                          {algorithm.energyRank})
                        </Typography>
                        {(algorithm.isPerfectWinner || algorithm.isBestAlternative) && (
                          <Chip
                            label={
                              algorithm.isPerfectWinner
                                ? 'Perfect Winner'
                                : 'Best Alternative'
                            }
                            size="small"
                            color={algorithm.isPerfectWinner ? 'success' : 'warning'}
                            sx={{ ml: 'auto' }}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            );
          }}
        />
      </TabPanel>

      {/* Energy Box Plot */}
      <TabPanel value={value} index={13}>
        <ChartPanelCard
          title="Statistical Distribution (Box Plot)"
          info={
            <>
              This box plot visualizes the statistical distribution across multiple runs for each
              algorithm.
              <strong> The box represents the interquartile range (IQR, 25th-75th percentile)</strong>,
              the horizontal line inside the box shows the <strong>median</strong>, and the
              whiskers extend to the minimum and maximum values.
              <strong> For Energy: Lower boxes indicate better (more energy-efficient) algorithms</strong>.
              <strong> For Fitness: Higher boxes indicate better solutions</strong>. Smaller box
              heights indicate more consistent performance across runs. This visualization is ideal
              for comparing both performance and reliability of algorithms.
            </>
          }
          renderContent={(isExpanded) => {
            const chartHeight = isExpanded
              ? EXPANDED_BOX_PLOT_CHART_HEIGHT
              : BOX_PLOT_CHART_HEIGHT;

            if (!results || !results.statistics) {
              return renderEmptyState(
                'Box plot requires multiple runs with statistics. Please run optimization with numberOfRuns > 1.',
                chartHeight
              );
            }

            if (energyBoxPlotData.length === 0) {
              return renderEmptyState(
                'No statistical data available for box plot visualization.',
                chartHeight
              );
            }

            return (
              <Box>
                <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label="Energy (Primary Metric)"
                    color="primary"
                    variant="filled"
                    sx={{ fontWeight: 600 }}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ alignSelf: 'center', ml: 1 }}
                  >
                    Showing energy consumption distribution - the primary optimization objective
                  </Typography>
                </Box>

                <BoxPlotChart data={energyBoxPlotData} height={chartHeight} />

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Statistical Summary
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: 2,
                    }}
                  >
                    {energyBoxPlotData.map((data) => (
                      <Paper
                        key={data.name}
                        variant="outlined"
                        sx={{ p: 1.5, bgcolor: 'background.default' }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {data.name}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
                          Median: {data.median.toFixed(2)} J
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
                          Mean: {data.mean.toFixed(2)} J
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
                          Std Dev: {data.stdDev.toFixed(2)} J
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
                          Range: [{data.min.toFixed(2)}, {data.max.toFixed(2)}]
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Box>
              </Box>
            );
          }}
        />
      </TabPanel>
    </Box>
  );
};

export default ChartsView;
